import { useState, useEffect, useMemo } from 'react';
import { FileText, Upload, RefreshCw, Download, Cloud, X, Loader2, ClipboardCheck, Paperclip, AlertCircle, RotateCcw, CheckCircle2, Clock, Scissors, PencilLine, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import VoucherSearch from './VoucherSearch.jsx';
import AttachQueue from './AttachQueue';
import ReviewQueue from './ReviewQueue.jsx';
import { cn, confirmToast } from '../lib/utils';
import { toast } from 'react-toastify';
import axios from 'axios';


const uploadFiles = process.env.REACT_APP_UPLOAD_FILES;
const testingBase = process.env.REACT_APP_TESTING_BASE;
const prodBase = process.env.REACT_APP_API_BASE;

// Microsoft Graph hands back a folder's children one page at a time and defaults
// to 200 per page. /api/grabuploaded asks for the first page and never follows
// @odata.nextLink, so a busy inbox arrives here already cut off. We cannot see
// the real total from the browser, but a count sitting exactly on a page
// boundary is the tell, and saying nothing is how "(200)" ended up reading like
// a complete inventory next to "502 in review".
const ONEDRIVE_PAGE_SIZE = 200;

export function MultiFileManager({
  files = [],
  onFileSelect,
  onRemoveDrafts,
  selectedFileIndex,
  onRefreshFiles,
  onSelectedFilesChange,
  isLoadingFiles,
  isLoadingInvoices,
  onDownloadInvoices,
  onInvoiceSelect,
  activeTab: externalActiveTab,
  onTabChange,
  useremail,
  reviewCount = 0,
  attachCount = 0,
  onAttachChanged,
  onRetryQueue,
  onIndexManually,
  onOpenSplitter,
  getQueueStatus,
  queueCounts = { reading: 0, queued: 0, error: 0, filed: 0, total: 0 },
  onReviewChanged
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [invoiceResults, setInvoiceResults] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  // const [activeTab, setActiveTab] = useState('uploaded'); // 'uploaded' or 'invoices'
  const [isSearching, setIsSearching] = useState(false);
  const [internalActiveTab, setInternalActiveTab] = useState('uploaded');
  const [fileSearch, setFileSearch] = useState('');

  const activeTab = externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;

  // Memoized so the two lists derived from it below can declare it as a
  // dependency honestly. Rebuilt inline it was a new array on every keystroke,
  // which would have made every downstream useMemo decorative.
  const nonLocalFiles = useMemo(
    () => files.filter(f => f.source !== 'local' && !(typeof File !== 'undefined' && f instanceof File)),
    [files]
  );

  const fileQuery = fileSearch.trim().toLowerCase();
  const isFiltering = fileQuery.length > 0;

  // Substring, not prefix: the clerk is looking at a paper invoice and typing
  // the fragment they can read off it - "0005", "D9LS" - not the front of
  // "082026_D9LS_1_0005.pdf".
  const visibleFiles = useMemo(() => {
    if (!fileQuery) return nonLocalFiles;
    return nonLocalFiles.filter((f) => (f?.name || '').toLowerCase().includes(fileQuery));
  }, [nonLocalFiles, fileQuery]);

  // The rendered list is filtered; everything downstream indexes into `files`.
  // Resolved once per render rather than an indexOf scan per row.
  //
  // Scoped to what is on screen. "Retry 12 failed" while a search hides ten of
  // them would fire off work the operator cannot see - the same mismatch
  // between a number and the rows under it that made "(200)" untrustworthy.
  // With no search active this is the whole list, exactly as before.
  const failedFiles = useMemo(
    () => visibleFiles.filter((f) => getQueueStatus?.(f.id)?.state === 'error'),
    [visibleFiles, getQueueStatus]
  );

  // A page-boundary count means Graph almost certainly had more to give. It can
  // be a false alarm on a folder that really holds exactly 200, which is why
  // this hedges ("may be more") instead of asserting a number it cannot know.
  const maybeTruncated =
    nonLocalFiles.length > 0 && nonLocalFiles.length % ONEDRIVE_PAGE_SIZE === 0;

  const indexById = useMemo(() => {
    const map = new Map();
    files.forEach((f, i) => { if (f?.id != null && !map.has(f.id)) map.set(f.id, i); });
    return map;
  }, [files]);
  const currentFiles = activeTab === 'uploaded' ? visibleFiles : '';
  const isLoading = activeTab === 'uploaded' ? isLoadingFiles : "Not implemented.";

  
  


  // Effect to handle PDF preview when exactly one file is selected
  useEffect(() => {
    if (files.length > 0 && !selectedFileIndex && selectedFileIndex !== 0) {
      const firstFile = files[0];
      console.log('Auto-selecting file for preview:', firstFile.name, 'source:', firstFile.source);
      console.log('Full selectedFile object:', firstFile);

      const fileSource = firstFile.source || (firstFile instanceof File ? 'local' : 'onedrive');

      onFileSelect({
        file: firstFile,
        index: 0,
        source: fileSource
      });
    }
  }, [files, selectedFileIndex, onFileSelect]);

  const uploadToOneDrive = async (file) => {
    try {
      //const response = await axios.post(`${testingBase}${uploadFiles}`, file, {
        const response = await axios.post(`${prodBase}${uploadFiles}`, file, {
        headers: {
          'Content-Type': 'application/pdf',
          'x-file-name': file.name,
          'x-file-size': file.size.toString()
        },
        body: file
      });

      return response.data

    } catch (error) {
      console.error('Error uploading file:', error);

      if (error.response) {
        const errorMessage = error.response.data?.error || `Upload failed with status ${error.response.status}`;
        throw new Error(errorMessage);
      } else if (error.request) {
        throw new Error('No response from server');
      } else {
        throw new Error(error.message || 'Upload failed');
      }
    }
  };

  const handleRefresh = async () => {
    if (activeTab === 'uploaded') {
      onRefreshFiles?.();
    } else {
      console.log('Refreshing invoice files...');
    }
  };

  const toggleInvoiceSelection = (index) => {
    setSelectedInvoices((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(index)) {
        newSelection.delete(index);
      } else {
        newSelection.add(index);
      }
      return newSelection;
    });
  };

  //function to change view of selected file but NOT mark it as selected
  const changeFileView = (index) => {
    if (onFileSelect) {
      const file = files[index];
      const source = (file.source === 'local' || file instanceof File) ? 'local' : 'onedrive';
      onFileSelect({ file, index, source });
    }
  };

  const handleFileUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) return;

    try {
      for (const file of selectedFiles) {
        if (file.type !== 'application/pdf') {
          console.warn(`Skipping non-PDF file: ${file.name}`);
          continue;
        }

        console.log(`Uploading ${file.name} to OneDrive...`);
        await uploadToOneDrive(file);
        console.log(`Successfully uploaded ${file.name} to OneDrive`);
      }

      // Refresh OneDrive files after upload
      await handleRefresh();

    } catch (error) {
      console.error('File upload error:', error);
    }

    event.target.value = '';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOverUpload = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDropUpload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    try {
      for (const file of droppedFiles) {
        if (file.type !== 'application/pdf') {
          console.warn(`Skipping non-PDF file: ${file.name}`);
          continue;
        }

        console.log(`Uploading ${file.name} to OneDrive...`);
        await uploadToOneDrive(file);
        console.log(`Successfully uploaded ${file.name} to OneDrive`);
      }

      // Refresh OneDrive files after upload
      await handleRefresh();

    } catch (error) {
      console.error('File upload error:', error);
    }
  };

  const formatFileSize = (file) => {
    // Get size from multiple possible properties
    const bytes = file?.size || file?.length || file?.fileSize || 0;

    // Handle invalid or missing size data
    if (!bytes || isNaN(bytes) || bytes === 0) {
      // For uploaded files, try to estimate or show unknown
      if (file instanceof File) {
        return file.size ? formatBytes(file.size) : 'Unknown size';
      }
      return 'Unknown size';
    }

    return formatBytes(bytes);
  };

  const formatBytes = (bytes) => {
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // Ensure i is within bounds
    const sizeIndex = Math.min(i, sizes.length - 1);
    const size = parseFloat((bytes / Math.pow(k, sizeIndex)).toFixed(2));

    return `${size} ${sizes[sizeIndex]}`;
  };

  const handleDeleteSinglePart = (partId) => {
    const partFile = files.find(f => f.id === partId);
    if (!partFile) return;

    if (window.confirm(`Delete ${partFile.name}? This cannot be undone.`)) {
      // Call parent's remove handler
      if (onRemoveDrafts) {
        onRemoveDrafts([partId]);
      }
    }
  };

  // Count draft files
  const draftFiles = files.filter(f =>
    f.source === 'temp-split' ||
    f.source === 'temp-part' ||
    f.isTemporary
  );
  const draftCount = draftFiles.length;

  // Delete all draft files
  const handleDeleteDrafts = async () => {
    if (draftCount === 0) return;

    const ok = await confirmToast({
      title: `Delete ${draftCount} draft file${draftCount > 1 ? 's' : ''}?`,
      message: 'This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      className: 'info-toast', // matches your styling class
    });

    if (!ok) return;

    const draftIds = draftFiles.map(f => f.id);

    // Call parent's remove drafts handler
    onRemoveDrafts?.(draftIds);

  };

  useEffect(() => {
    if (activeTab === 'uploaded') setSelectedInvoices(new Set());
    // The file selection deliberately survives a tab switch: a ticked file is
    // queued for review, and walking over to the Review tab to approve it must
    // not silently untick it.
  }, [activeTab]);


  const handleDownloadSelected = () => {
    if (selectedInvoices.size === 0) return;

    const invoicesToDownload = Array.from(selectedInvoices).map(index => invoiceResults[index]);
    handleDownloadInvoices?.(invoicesToDownload);
  };

    const handleDownloadInvoices = async (invoicesToDownload) => {
  try {
    for (const invoice of invoicesToDownload) {
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = invoice.downloadUrl || invoice.webUrl;
      link.download = invoice.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Small delay between downloads to avoid browser blocking
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    toast(`Downloading ${invoicesToDownload.length} invoice(s)`, {
      className: 'success-toast',
      autoClose: 3000
    });
  } catch (error) {
    console.error('Error downloading invoices:', error);
    toast('Failed to download invoices', {
      className: 'error-toast',
      autoClose: 3000
    });
  }
};

  const handleTabChange = (newTab) => {
    if (onTabChange) {
      onTabChange(newTab);
    } else {
      setInternalActiveTab(newTab);
    }
  };


  // return (
  //   <Card >
  //     <CardContent className="space-y-6">
  //       {/* Two-Column Layout for File Sections */}
  //       <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
  //         {/* OneDrive Files Section */}
  //         <div>

  //           {files.filter(f => f.source !== 'local' && !(f instanceof File)).length === 0 ? (
  //             <div className="text-center py-6 border-2 border-lined border-gray-200 rounded-lg">
  //               <p className="text-sm text-muted-foreground">No OneDrive files</p>
  //               <p className="text-xs text-muted-foreground mt-1">Files will appear here automatically</p>
  //             </div>
  //           ) : (
  //             <div className="max-h-64 overflow-y-auto space-y-2">
  //               {files.map((file, index) => {
  //                 const isLocal = file.source === 'local' || file instanceof File;
  //                 const isTemporary = file.source === 'temp-split' || (file.isTemporary && !file.isPartFile);
  //                 const isPartFile = (file.source === 'temp-part' || file.isPartFile) && file.isTemporary;

  //                 if (isLocal) return null;

  //                 const isActive = selectedFileIndex === index;
  //                 const isSelected = selectedFiles.has(index);

  //                 return (
  //                   <div
  //                     key={`onedrive-${file.id || file.name}-${index}`}
  //                     className={cn(
  //                       "flex items-center gap-3 p-3 border rounded-lg transition-all duration-200 cursor-pointer",
  //                       isActive && isTemporary ? "border-green-500 bg-green-100 ring-2 ring-green-200" :
  //                         isActive && isPartFile ? "border-yellow-500 bg-yellow-100 ring-2 ring-yellow-200" :
  //                           isActive ? "border-blue-500 bg-blue-100 ring-2 ring-blue-200" :
  //                             isTemporary ? "border-green-300 bg-green-50" :
  //                               isPartFile ? "border-yellow-300 bg-yellow-50" :
  //                                 isSelected ? "border-blue-300 bg-blue-50" :
  //                                   "border-border hover:border-blue-200 hover:bg-blue-25"
  //                     )}
  //                     onClick={(e) => {
  //                       if (e.target.closest('button')) return;
  //                       changeFileView(index);
  //                     }}
  //                   >
  //                     {/* Only show checkbox if NOT a PART file */}
  //                     {!isPartFile && (
  //                       <Checkbox
  //                         checked={isSelected}
  //                         onClick={(e) => {
  //                           e.stopPropagation();
  //                           toggleFileSelection(index);
  //                         }}
  //                       />
  //                     )}

  //                     {/* For PART files, show a trash button instead */}
  //                     {isPartFile && (
  //                       <Button
  //                         variant="ghost"
  //                         size="sm"
  //                         className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
  //                         onClick={(e) => {
  //                           e.stopPropagation();
  //                           handleDeleteSinglePart(file.id);
  //                         }}
  //                       >
  //                         <X className="h-4 w-4" />
  //                       </Button>
  //                     )}

  //                     <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded">
  //                       <img
  //                         src="https://slwelding.blob.core.windows.net/slwcontainer/assets/pdf-icon.svg"
  //                         alt="PDF"
  //                         className="w-4 h-4 flex-shrink-0"
  //                       />
  //                     </div>
  //                     <div className="flex-1 min-w-0">
  //                       <div className="flex items-center gap-2">
  //                         <span className="font-medium text-sm truncate">{file.name}</span>
  //                         {isTemporary ? (
  //                           <Badge variant="secondary" className="text-xs bg-green-600 text-white">
  //                             Draft
  //                           </Badge>
  //                         ) : isPartFile ? (
  //                           <Badge variant="secondary" className="text-xs bg-yellow-600 text-white">
  //                             PART
  //                           </Badge>
  //                         ) : (
  //                           <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
  //                             OneDrive
  //                           </Badge>
  //                         )}
  //                         {isActive && (
  //                           <Badge variant="default" className={cn(
  //                             "text-xs",
  //                             isTemporary ? "bg-green-700 text-white" :
  //                               isPartFile ? "bg-yellow-700 text-white" :
  //                                 "bg-blue-600 text-white"
  //                           )}>
  //                             Active
  //                           </Badge>
  //                         )}
  //                       </div>
  //                       <p className="text-xs text-muted-foreground">
  //                         {formatFileSize(file)} • {
  //                           isTemporary ? 'Draft - not saved yet' :
  //                             isPartFile ? `Auto-uploads with draft` :
  //                               'From OneDrive'
  //                         }
  //                       </p>
  //                     </div>
  //                   </div>
  //                 );
  //               })}
  //             </div>
  //           )}
  //         </div>
  //       </div>

  //       {/* Action Buttons */}
  //       <div className="pt-4 border-t">
  //         <div className="flex items-center justify-between">
  //           <div className="text-sm text-muted-foreground">
  //             {selectedFiles.size > 0
  //               ? `${selectedFiles.size} files selected for processing`
  //               : selectedFileIndex !== null
  //                 ? `File "${files[selectedFileIndex]?.name}" is active for processing`
  //                 : 'Select files to process individually or combine with PDF splitter'
  //             }
  //           </div>
  //           <div className="flex gap-2">
  //             {draftCount > 0 && (
  //               <Button
  //                 variant="destructive"
  //                 size="sm"
  //                 onClick={handleDeleteDrafts}
  //               >
  //                 Delete {draftCount} Draft{draftCount > 1 ? 's' : ''}
  //               </Button>
  //             )}
  //             {files.length > 0 && (
  //               <Button
  //                 variant="outline"
  //                 size="sm"
  //                 onClick={() => {
  //                   const newSelected = selectedFiles.size === files.length
  //                     ? new Set()
  //                     : new Set(files.map((_, i) => i));

  //                   setSelectedFiles(newSelected);

  //                   if (onSelectedFilesChange) {
  //                     onSelectedFilesChange(Array.from(newSelected));
  //                   }
  //                 }}
  //               >
  //                 {selectedFiles.size === files.length ? 'Deselect All' : 'Select All'}
  //               </Button>
  //             )}
  //           </div>
  //         </div>
  //       </div>
  //     </CardContent>
  //   </Card>
  // );


  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-5">
          <div className="flex-[1] min-w-0">
            <CardTitle className="flex items-center gap-1">
              <FileText className="h-5 w-5" />
              Document Manager
            </CardTitle>
            <CardDescription className="pt-3 flex flex-col items-center gap-2">
              <p>Manage your files from OneDrive and upload local files to OneDrive.</p>
              <p>Select files to process individually or combine them using the PDF splitter.</p>
            </CardDescription>
          </div>

          {/* Upload Area */}
          <div className="flex-[2] ml-4">
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-4 text-center transition-colors duration-200 h-full",
                isDragOver ? "border-primary bg-accent/10" : "border-border hover:border-primary/50"
              )}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOverUpload}
              onDrop={handleDropUpload}
            >
              <label htmlFor="add-files" className="cursor-pointer">
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2">
                    <Upload className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <span className="text-xl font-bold">Upload Documents To OneDrive</span>
                  <span className="text-xs text-muted-foreground text-center">
                    Upload from your computer then refresh below to view OneDrive files
                  </span>
                </div>
                <Input
                  id="add-files"
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                  accept="application/pdf"
                  multiple
                />
              </label>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-4 border-b">
          <button
            onClick={() => { handleTabChange('uploaded'); console.log('Switched to Uploaded tab'); }}
            className={`px-4 py-2 font-medium transition-colors relative ${activeTab === 'uploaded'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
              }`}
          >
            <div className="flex items-center gap-2">
              Uploaded Files
            </div>
          </button>
          <button
            onClick={() => { handleTabChange('review'); }}
            className={`px-4 py-2 font-medium transition-colors relative ${activeTab === 'review'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
              }`}
          >
            <div className="flex items-center gap-2">
              Review Queue
              {reviewCount > 0 && (
                <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white tabular-nums dark:bg-amber-600">
                  {reviewCount}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => { handleTabChange('archive'); }}
            className={`px-4 py-2 font-medium transition-colors relative ${activeTab === 'archive'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
              }`}
          >
            <div className="flex items-center gap-2">
              Search Archive
              {attachCount > 0 && (
                <span className="rounded-full bg-blue-500 px-2 py-0.5 text-xs font-semibold text-white tabular-nums dark:bg-blue-600">
                  {attachCount}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => { handleTabChange('invoices'); console.log('Switched to Invoices tab'); }}
            className={`px-4 py-2 font-medium transition-colors relative ${activeTab === 'invoices'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
              }`}
          >
            <div className="flex items-center gap-2">
              Invoices
            </div>
          </button>
        </div>

        {activeTab === 'review' && (
          <ReviewQueue
            useremail={useremail}
            onFiled={() => {
              onRefreshFiles?.();
              onReviewChanged?.();
              // Filing an invoice can place packing slips that were waiting on
              // that voucher, so the badge beside Search Archive has to follow.
              onAttachChanged?.();
            }}
          />
        )}

        {activeTab === 'archive' && (
          <AttachQueue
            useremail={useremail}
            onAttached={() => {
              onRefreshFiles?.();
              onAttachChanged?.();
            }}
          />
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {activeTab === 'uploaded' && (
            <div className="flex w-full flex-wrap items-center gap-2 mt-[1rem] mb-3">
              <Cloud className="h-5 w-5 text-blue-600 mr-1" />
              <h3 className="font-semibold flex items-center gap-2">
                OneDrive Files
                {/* The first number is always the number of rows below it. While
                    a search is on, "3 of 200" says which 200 the 3 came out of;
                    without the "of", a shrinking count next to an unchanged
                    list is what makes people distrust the whole screen. */}
                <span className="tabular-nums font-normal text-muted-foreground" aria-live="polite">
                  {isFiltering
                    ? `(${visibleFiles.length} of ${nonLocalFiles.length} loaded)`
                    : `(${nonLocalFiles.length} loaded)`}
                </span>
              </h3>

              {maybeTruncated && (
                <span
                  className="flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
                  title={`OneDrive returns folder contents ${ONEDRIVE_PAGE_SIZE} at a time and this list stops on that boundary, so the inbox may hold more than ${nonLocalFiles.length} files. Searching below only looks at the ${nonLocalFiles.length} loaded here.`}
                >
                  <AlertCircle className="h-3 w-3" aria-hidden="true" />
                  May be more than {nonLocalFiles.length}
                </span>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoadingFiles}
                aria-label="Refresh OneDrive files"
              >
                <RefreshCw className={cn("h-4 w-4 text-blue-600", isLoadingFiles && "animate-spin")} />
              </Button>

              {/* Searching a page of 200 that scrolls inside a 400px box beats
                  scrolling it. Filters the list only - the document open in the
                  preview stays open. */}
              <div className="relative w-full sm:w-64">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500"
                  aria-hidden="true"
                />
                <Input
                  type="text"
                  value={fileSearch}
                  onChange={(e) => setFileSearch(e.target.value)}
                  onKeyDown={(e) => {
                    // Escape clears rather than blurs: the list under the box is
                    // the thing you want back, and reaching for the mouse to get
                    // it is the annoying part.
                    if (e.key === 'Escape' && fileSearch) {
                      e.preventDefault();
                      setFileSearch('');
                    }
                  }}
                  placeholder="Search file names"
                  aria-label="Search OneDrive files by file name"
                  className="h-9 pl-8 pr-9 text-sm"
                />
                {fileSearch && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                    onClick={() => setFileSearch('')}
                    aria-label="Clear file name search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>

              {/* Reading happens without being asked, so this reports rather
                  than offers: where the batch has got to, and what needs a
                  second look. */}
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {queueCounts.total > 0 && (
                  <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium tabular-nums dark:border-slate-800 dark:bg-slate-900">
                    {queueCounts.reading > 0 && (
                      <span className="flex items-center gap-1 text-blue-700 dark:text-blue-300">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Reading {queueCounts.reading}
                      </span>
                    )}
                    {queueCounts.waiting > 0 && (
                      <span className="text-slate-500 dark:text-slate-400">
                        {queueCounts.waiting} queued
                      </span>
                    )}
                    {queueCounts.queued > 0 && (
                      <span className="text-amber-700 dark:text-amber-300">
                        {queueCounts.queued} in review
                      </span>
                    )}
                    {queueCounts.error > 0 && (
                      <span className="font-semibold text-red-700 dark:text-red-300">
                        {queueCounts.error} failed
                      </span>
                    )}
                    {queueCounts.reading === 0 &&
                      queueCounts.waiting === 0 &&
                      queueCounts.error === 0 && (
                        <span className="text-emerald-700 dark:text-emerald-400">All read</span>
                      )}
                  </div>
                )}

                {failedFiles.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300"
                    onClick={() => failedFiles.forEach((f) => onRetryQueue?.(f))}
                  >
                    <RotateCcw className="mr-1 h-3.5 w-3.5" />
                    Retry {failedFiles.length} failed{isFiltering ? ' in results' : ''}
                  </Button>
                )}
              </div>
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="flex flex-col items-start mb-3">
              <div className="flex items-center mt-[2rem] mb-[2rem]">
                <Cloud className="h-5 w-5 text-blue-600 mr-3" />
                <h3 className="font-semibold flex items-center gap-2 mr-[1rem]">
                  Invoices Files Search
                </h3>
                <p className="text-sm text-muted-foreground">
                  Search for invoices by file name or date.
                </p>
              </div>

              <VoucherSearch onResults={(invoiceFiles) => {
                // optional: sync with your parent state/table if you need
                setInvoiceResults(invoiceFiles);
                setSelectedInvoices(new Set());
                console.log("Search results:", invoiceFiles);
              }} onFileSelect={onInvoiceSelect}/>
            </div>
          )}

        </div>

        {/* Files List */}
        <div className={cn('space-y-2 max-h-[400px] overflow-y-auto', activeTab === 'review' && 'hidden')}>
          {currentFiles.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {/* An empty list after a search is not the same news as an empty
                  inbox. Told the first way, the operator refreshes and files a
                  bug; told the second, they fix their own typo. */}
              {activeTab === 'uploaded' && isFiltering ? (
                <div className="flex flex-col items-center gap-2">
                  <Search className="h-5 w-5 text-slate-400 dark:text-slate-500" aria-hidden="true" />
                  <p className="text-sm">
                    No file name contains{' '}
                    <span className="font-medium text-foreground">"{fileSearch.trim()}"</span>
                  </p>
                  <p className="text-xs">
                    Searched the {nonLocalFiles.length} file{nonLocalFiles.length === 1 ? '' : 's'} loaded from OneDrive
                    {maybeTruncated ? '. The inbox may hold more that have not loaded.' : '.'}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => setFileSearch('')}>
                    <X className="mr-1 h-3.5 w-3.5" />
                    Clear search
                  </Button>
                </div>
              ) : activeTab === 'uploaded' ? (
                'No files uploaded'
              ) : (
                ''
              )}
            </div>
          ) : activeTab === 'uploaded' ? (
            <>
            {currentFiles.map((file, index) => {
              const isLocal =
                file?.source === "local" || (typeof File !== "undefined" && file instanceof File);
              const isTemporary =
                file?.source === "temp-split" || (file?.isTemporary && !file?.isPartFile);
              const isPartFile =
                (file?.source === "temp-part" || file?.isPartFile) && file?.isTemporary;

              if (isLocal) return null; // skip true locals in uploaded view

              // `index` walks the filtered list, so it is only a usable stand-in
              // for a position in `files` when nothing is being filtered out.
              // Guessing while a search is on would open a different document
              // than the row that was clicked. MainView always sets an id, so
              // the lookup carries every real case.
              const filesIndex = indexById.get(file.id) ?? (isFiltering ? -1 : index);
              const isActive = selectedFileIndex === filesIndex;
              const queueStatus = getQueueStatus?.(file.id) || null;
              const busyReading =
                queueStatus?.state === "reading" || queueStatus?.state === "waiting";
              const decided =
                queueStatus?.state === "rejected" || queueStatus?.state === "filed";
              // A read that failed, or one a human already sent back, is where
              // typing it in by hand is the obvious next move.
              const needsHelp =
                queueStatus?.state === "error" || queueStatus?.state === "rejected";

        return (
          <div
            key={`onedrive-${file.id || file.name}-${index}`}
            className={cn(
              "flex items-center gap-3 p-3 border rounded-lg transition-all duration-200 cursor-pointer",
              isActive && isTemporary
                ? "border-green-500 bg-green-100 ring-2 ring-green-200"
                : isActive && isPartFile
                ? "border-yellow-500 bg-yellow-100 ring-2 ring-yellow-200"
                : isActive
                ? "border-blue-500 bg-blue-100 ring-2 ring-blue-200"
                : isTemporary
                ? "border-green-300 bg-green-50"
                : isPartFile
                ? "border-yellow-300 bg-yellow-50"
                : "border-border hover:border-blue-200 hover:bg-blue-25"
            )}
            onClick={(e) => {
              // ignore if clicking a button/checkbox inside
              if (e.target.closest("button") || e.target.closest('[role="checkbox"]')) return;
              if (filesIndex < 0) return;
              changeFileView?.(filesIndex);
            }}
          >
            {/* No checkbox: every uploaded file is read automatically, so there
                is nothing to opt in to. What is left is a way to ask for
                another look when a read came back wrong. */}

            {/* For PART files, show a trash button instead */}
            {isPartFile && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSinglePart?.(file.id);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}

            <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded">
              <img
                src="https://slwelding.blob.core.windows.net/slwcontainer/assets/pdf-icon.svg"
                alt="PDF"
                className="w-4 h-4 flex-shrink-0"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm truncate">{file.name}</span>
                {isTemporary ? (
                  <Badge variant="secondary" className="text-xs bg-green-600 text-white">
                    Draft
                  </Badge>
                ) : isPartFile ? (
                  <Badge variant="secondary" className="text-xs bg-yellow-600 text-white">
                    PART
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                    OneDrive
                  </Badge>
                )}
                {isActive && (
                  <Badge
                    variant="default"
                    className={cn(
                      "text-xs",
                      isTemporary
                        ? "bg-green-700 text-white"
                        : isPartFile
                        ? "bg-yellow-700 text-white"
                        : "bg-blue-600 text-white"
                    )}
                  >
                    Active
                  </Badge>
                )}

                {/* What ticking this file actually did. The operator should
                    never have to open another tab to find out. */}
                {queueStatus?.state === "waiting" && (
                  <Badge variant="outline" className="gap-1 border-slate-300 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                    <Clock className="h-3 w-3" />
                    Queued to read
                  </Badge>
                )}
                {queueStatus?.state === "reading" && (
                  <Badge variant="outline" className="gap-1 border-blue-300 text-xs text-blue-700 dark:border-blue-800 dark:text-blue-300">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Reading
                  </Badge>
                )}
                {queueStatus?.state === "removing" && (
                  <Badge variant="outline" className="gap-1 border-slate-300 text-xs text-slate-600">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Removing
                  </Badge>
                )}
                {/* A scan of nothing but packing slips is not in review at
                    all - it is over in Search Archive waiting for a voucher.
                    Saying "In review" would send the clerk to the wrong tab. */}
                {queueStatus?.state === "queued" &&
                  queueStatus.slipCount > 0 &&
                  queueStatus.slipCount === queueStatus.segmentCount && (
                    <Badge variant="outline" className="gap-1 border-blue-300 bg-blue-50 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300">
                      <Paperclip className="h-3 w-3" />
                      {queueStatus.segmentCount > 1
                        ? `Packing slips · ${queueStatus.segmentCount}`
                        : "Packing slip"}
                    </Badge>
                  )}
                {queueStatus?.state === "queued" &&
                  !(queueStatus.slipCount > 0 && queueStatus.slipCount === queueStatus.segmentCount) && (
                  <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
                    <ClipboardCheck className="h-3 w-3" />
                    {/* The count is how many invoices are in this one scan, not
                        how many times it was read. "(2)" read like a retry
                        counter, which is the opposite of reassuring. */}
                    {queueStatus.segmentCount > 1
                      ? `In review · ${queueStatus.segmentCount} invoices`
                      : "In review"}
                  </Badge>
                )}
                {queueStatus?.state === "rejected" && (
                  <Badge variant="outline" className="gap-1 border-slate-300 bg-slate-50 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                    <X className="h-3 w-3" />
                    Rejected
                  </Badge>
                )}
                {queueStatus?.state === "filed" && (
                  <Badge variant="outline" className="gap-1 border-emerald-300 bg-emerald-50 text-xs text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                    <CheckCircle2 className="h-3 w-3" />
                    Already filed
                  </Badge>
                )}
                {queueStatus?.state === "error" && (
                  <Badge variant="outline" className="gap-1 border-red-300 bg-red-50 text-xs text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
                    <AlertCircle className="h-3 w-3" />
                    Read failed
                  </Badge>
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                {file.size ? `${(file.size / 1024).toFixed(1)} KB` : "Unknown size"} •{" "}
                {isTemporary ? "Draft - not saved yet" : isPartFile ? "Auto-uploads with draft" : "From OneDrive"}
                {queueStatus?.state === "error" && (
                  <span className="text-red-600 dark:text-red-400"> • {queueStatus.error}</span>
                )}
              </p>
            </div>

            {!isPartFile && (
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                  title="Split this PDF into separate invoices"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSplitter?.(file);
                  }}
                >
                  <Scissors className="h-3.5 w-3.5" />
                  <span className="ml-1 hidden sm:inline">Split</span>
                </Button>

                {/* The override. Sends the scan to review with an empty form
                    and no model call, for anything the reader cannot handle. */}
                <Button
                  variant={needsHelp ? "default" : "ghost"}
                  size="sm"
                  disabled={busyReading}
                  className={cn(
                    "h-8 px-2 text-xs",
                    needsHelp
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  title="Index this scan by hand - opens it in Review with a blank form, no AI read"
                  onClick={(e) => {
                    e.stopPropagation();
                    onIndexManually?.(file);
                  }}
                >
                  <PencilLine className="h-3.5 w-3.5" />
                  <span className="ml-1 hidden md:inline">Enter manually</span>
                </Button>

                {/* The button reports its own state. A read takes ~20s, so a
                    button that looked idle the whole time invited a second and
                    third press on a document that was already being read. */}
                <Button
                  variant={queueStatus?.state === "error" ? "default" : "ghost"}
                  size="sm"
                  disabled={busyReading}
                  className={cn(
                    "h-8 min-w-[4.75rem] px-2 text-xs",
                    queueStatus?.state === "error"
                      ? "bg-red-600 text-white hover:bg-red-700"
                      : busyReading
                      ? "text-blue-700 dark:text-blue-300"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  title={
                    decided
                      ? "Already decided. Retry reads it again and puts it back in review."
                      : busyReading
                      ? "Reading this document now"
                      : queueStatus?.state === "error"
                      ? `Read failed: ${queueStatus.error}. Try again.`
                      : "Read this document again and replace what is in the review queue"
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onRetryQueue?.(file);
                  }}
                >
                  {busyReading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5" />
                  )}
                  <span className="ml-1 hidden sm:inline">
                    {queueStatus?.state === "reading"
                      ? "Reading"
                      : queueStatus?.state === "waiting"
                      ? "Queued"
                      : "Retry"}
                  </span>
                </Button>
              </div>
            )}
          </div>
        );
            })}
            </>
          ) :  (
            <>
              {currentFiles.map((invoicesFile, index) => {
                const isSelected = selectedFileIndex === index;
                const isChecked = selectedInvoices.has(index);

                return (
                  <div
                    key={invoicesFile.id || index}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleInvoiceSelection?.(index)}
                      className="h-4 w-4 rounded border-gray-300"
                      onClick={(e) => e.stopPropagation()}
                    />

                    <div className="flex-1 flex items-center gap-3 cursor-pointer" onClick={() => onFileSelect?.(index)}>
                      <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{invoicesFile.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {invoicesFile.size ? `${(invoicesFile.size / 1024).toFixed(1)} KB` : "Unknown size"}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {invoicesFile.source === "onedrive" && <Badge variant="outline">OneDrive</Badge>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
</div>

        {activeTab === 'invoices' && selectedInvoices.size > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadSelected}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download Selected ({selectedInvoices.size})
          </Button>
        )}
      </CardContent>
    </Card>
  );
}